import { SettingsSection } from '@components'

import { Flex, Icon, Spacing, Switch, Text, Tooltip } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useTrackInvoicingSettings } from '@features/invoicingSettings/hooks/useTrackInvoicingSettings'
import {
  useUpdateOrganizationSettings,
  useUserOrganization,
  useUserOrganizationSettings,
} from '@modules-deprecated/app/organization'

export const PreferencesSettings = (): ReactElement => {
  const { t } = useTranslation()
  const { trackInvoicingSettings } = useTrackInvoicingSettings()
  const { organization } = useUserOrganization()
  const { organizationSettings } = useUserOrganizationSettings()
  const { update } = useUpdateOrganizationSettings()

  const isInvoiceApprovalConfirmationEnabled = useMemo(
    () => organizationSettings?.isInvoiceEditorApprovalModalDismissed !== '1',
    [organizationSettings?.isInvoiceEditorApprovalModalDismissed],
  )

  const enableInvoiceApprovalConfirmation = useCallback(
    (enabled: boolean) => {
      if (organization?.id) {
        update({
          key: 'isInvoiceEditorApprovalModalDismissed',
          organizationId: organization?.id,
          value: enabled ? '0' : '1',
        })
      }
    },
    [organization?.id, update],
  )

  const handleSwitchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked

      trackInvoicingSettings('Always show the message "Review Before Approving"', { checked })
      enableInvoiceApprovalConfirmation(checked)
    },
    [enableInvoiceApprovalConfirmation, trackInvoicingSettings],
  )

  return (
    <SettingsSection.Content title={t('settings.organization.external_invoice.preferences_settings.title')}>
      <Flex justifyContent="space-between">
        <Flex gap={Spacing.S8}>
          <Text colorVariant="secondary">
            {t('settings.organization.external_invoice.preferences_settings.enable_approval_confirmation')}
          </Text>
          <Tooltip
            label={t(
              'settings.organization.external_invoice.preferences_settings.enable_approval_confirmation.tooltip',
            )}
            placement="top"
          >
            <Icon icon="info" />
          </Tooltip>
        </Flex>
        <Switch checked={isInvoiceApprovalConfirmationEnabled} onChange={handleSwitchChange} />
      </Flex>
    </SettingsSection.Content>
  )
}
